<template>
  <div class="flex h-full flex-col bg-bgnew">
    <div v-if="isLoading || !place" class="flex flex-1 items-center justify-center text-sm text-txprimary/60">
      Загрузка...
    </div>

    <template v-else>
      <div class="min-h-0 flex-1 overflow-y-auto">
        <!-- Шапка: размытый аватар как подложка, поверх — кнопки и карточка канала -->
        <div class="relative overflow-hidden px-4 pb-5 pt-5">
          <div class="absolute inset-0 bg-bgnew">
            <img
              v-if="place.avatar"
              :src="place.avatar"
              alt=""
              class="absolute inset-0 size-full scale-125 object-cover blur-[15px]"
            />
          </div>

          <div class="relative flex items-center justify-between">
            <button
              type="button"
              class="flex h-10 items-center gap-1 rounded-xl bg-white pl-2.5 pr-[15px] text-sm text-primary"
              @click="emit('close')"
            >
              <!-- SVG вместо иконочного шрифта: у глифа ink смещён от базовой
                   линии, и он всегда сидит выше текста -->
              <svg viewBox="0 0 24 24" fill="none" class="size-5 shrink-0">
                <path
                  d="M14.5 6.5L9 12l5.5 5.5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="text-sm text-primary">Назад</span>
            </button>
            <div class="mt-2 flex items-center gap-1.5">
              <button
                type="button"
                class="channel-sidebar__round"
                title="Написать каналу"
                @click="askQuestion()"
              >
                <i class="fi fi-sr-comment-alt text-primary" />
              </button>
              <button
                type="button"
                class="channel-sidebar__round"
                :title="place.isFavorite ? 'Убрать из избранного' : 'В избранное'"
                @click="toggleFavorite"
              >
                <i
                  class="fi fi-sr-heart transition-colors"
                  :class="place.isFavorite ? 'text-primary' : 'text-txprimary/40'"
                />
              </button>
              <button
                type="button"
                class="channel-sidebar__round"
                title="Скопировать ссылку"
                @click="shareChannel"
              >
                <i class="fi fi-sr-share text-txprimary/40" />
              </button>
            </div>
          </div>

          <div class="relative mt-[45px]">
            <div
              class="absolute -top-[45px] left-1/2 z-[1] size-[70px] -translate-x-1/2 overflow-hidden rounded-full border-2 border-white bg-bgnew shadow-[0px_3px_12px_0px_rgba(49,118,255,0.14)]"
            >
              <NuxtImg
                v-if="place.avatar"
                :src="place.avatar"
                alt="Аватар канала"
                class="size-full object-cover"
              />
            </div>
            <div class="flex flex-col items-center gap-3 rounded-[20px] bg-white px-4 pb-4 pt-10">
              <div class="flex items-start justify-center gap-2.5">
                <h2 class="text-center text-xl font-bold leading-tight text-txprimary max-md:text-base">
                  {{ place.name }}
                </h2>
                <!-- !w-auto: глобальный стиль меню растягивает любую ссылку на всю ширину -->
                <NuxtLink
                  v-if="place.url"
                  :to="place.url"
                  target="_blank"
                  rel="nofollow noindex noopener noreferrer"
                  class="flex size-6 shrink-0 !w-auto items-center justify-center"
                  title="Открыть канал"
                >
                  <i class="fi fi-br-arrow-up-right text-xs leading-none text-primary [-webkit-text-stroke:1px] max-md:text-[10px]" />
                </NuxtLink>
              </div>
              <p
                v-if="place.description"
                class="text-center text-sm leading-normal text-txprimary/60 max-md:leading-snug"
              >
                {{ displayedDescription }}<span
                  v-if="isDescriptionTruncatable"
                  class="ml-1 cursor-pointer text-sm text-primary"
                  @click="isDescriptionExpanded = !isDescriptionExpanded"
                >{{ isDescriptionExpanded ? "Скрыть" : "Подробнее" }}</span>
              </p>
              <div v-if="place.categories?.length" class="flex flex-wrap justify-center gap-2">
                <span
                  v-for="category in place.categories"
                  :key="category.id"
                  class="rounded-lg border border-[#D9E5FF] px-2 py-1 text-sm leading-[1.3] text-txprimary max-md:text-xs"
                >
                  {{ category.name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-4 px-4 pb-4 pt-5">
          <!-- Подсказки: слайдер, листается сам раз в 5 секунд -->
          <div
            class="flex items-center gap-3 rounded-[20px] border border-primary/15 bg-white p-4"
            @mouseenter="stopSlides"
            @mouseleave="startSlides"
          >
            <div class="min-w-0 flex-1">
              <Transition name="channel-sidebar-slide" mode="out-in">
                <div :key="activeSlide" class="flex w-full items-start gap-3">
                  <span
                    class="flex size-[50px] shrink-0 items-center justify-center rounded-xl bg-primary/10 max-md:size-10"
                  >
                    <i class="fi text-2xl leading-none text-primary max-md:text-xl" :class="slide.icon" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-lg font-medium leading-[1.3] text-txprimary max-md:text-base">
                      {{ slide.title }}
                    </span>
                    <span class="mt-1 block text-[13px] leading-normal text-txprimary/60">{{ slide.text }}</span>
                  </span>
                </div>
              </Transition>
              <div class="mt-3 flex gap-1 pl-[62px] max-md:pl-[52px]">
                <button
                  v-for="(_, index) in slides"
                  :key="index"
                  type="button"
                  class="h-1.5 rounded-full transition-all"
                  :class="index === activeSlide ? 'w-[18px] bg-primary' : 'w-1.5 bg-primary/20'"
                  @click="goToSlide(index)"
                />
              </div>
            </div>
            <button
              type="button"
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 transition-colors hover:bg-primary/20"
              title="Следующая подсказка"
              @click="goToSlide((activeSlide + 1) % slides.length)"
            >
              <i class="fi fi-sr-angle-small-right translate-y-[1.5px] text-lg leading-none text-primary" />
            </button>
          </div>

          <!-- Вкладки -->
          <div class="flex gap-[13px] rounded-2xl bg-white p-1">
            <button
              v-for="(tab, index) in tabs"
              :key="tab"
              type="button"
              class="h-10 flex-1 rounded-xl px-2 text-center text-[13px] leading-[1.2] transition-colors max-md:text-xs"
              :class="activeTab === index ? 'bg-bgnew text-primary' : 'text-txprimary hover:text-primary'"
              @click="activeTab = index"
            >
              {{ tab }}
            </button>
          </div>

          <!-- Купить рекламу -->
          <div v-if="activeTab === 0" class="flex flex-col gap-2">
            <div
              v-for="price in sortedPrices"
              :key="price.priceType"
              class="flex h-[60px] items-center gap-3 rounded-2xl bg-white pl-6 pr-1.5"
            >
              <div class="flex min-w-0 flex-1 items-center gap-[5px]">
                <span class="truncate text-sm font-medium text-txprimary">
                  {{ getFormat(price).label }}
                </span>
                <UINewTooltipWithText placement="top">
                  <template #text>{{ getFormat(price).hint }}</template>
                  <i class="fi fi-sr-info block cursor-pointer text-lg leading-none text-txprimary/30" />
                </UINewTooltipWithText>
              </div>
              <span class="whitespace-nowrap text-base font-medium text-txprimary max-md:text-sm">
                {{ price.price.toLocaleString() }} ₽
              </span>
              <button
                v-if="!findBasketItem(price)"
                type="button"
                class="ml-3 flex h-[49px] w-[101px] shrink-0 items-center justify-center rounded-2xl bg-primary text-white transition-opacity hover:opacity-80 max-md:ml-2 max-md:w-20"
                @click="addToBasket(price)"
              >
                <i class="fi fi-sr-shopping-cart text-[22px] leading-none" />
              </button>
              <div
                v-else
                class="ml-3 flex h-[49px] w-[101px] shrink-0 items-center justify-between rounded-2xl bg-bgnew px-3 max-md:ml-2 max-md:w-20"
              >
                <button type="button" class="flex items-center" @click="decreaseQuantity(price)">
                  <IMinus class="size-4 fill-primary" />
                </button>
                <span class="text-sm text-txprimary">{{ findBasketItem(price)?.quantity || 1 }}</span>
                <button type="button" class="flex items-center" @click="increaseQuantity(price)">
                  <IPlus class="size-4 fill-primary" />
                </button>
              </div>
            </div>
            <div v-if="!sortedPrices.length" class="rounded-2xl bg-white p-4 text-center text-sm text-txprimary/60">
              Форматы размещения не указаны
            </div>
          </div>

          <!-- Расширенная статистика -->
          <div v-else-if="activeTab === 1" class="rounded-[20px] bg-white p-4">
            <ChannelViewStat :place="place" />
          </div>

          <!-- Другие каналы владельца -->
          <ChannelViewOtherChannels
            v-else
            :user-id="place.userId"
            :place-id="place.id"
            accent-color="#3176FF"
            compact
            @message="askQuestion"
          />
        </div>
      </div>

      <div class="flex shrink-0 gap-3 px-4 pb-4 pt-2">
        <button
          type="button"
          class="h-[50px] flex-1 rounded-2xl bg-white text-sm text-primary transition-colors hover:bg-primary/10"
          @click="askQuestion()"
        >
          Написать каналу
        </button>
        <button
          v-if="defaultPrice && basketTotal"
          type="button"
          class="flex h-[50px] flex-1 items-center justify-center gap-2 rounded-2xl bg-primary/10 text-sm text-primary transition-colors hover:bg-primary/15"
          @click="basketStore.isBasketDialogOpen = true"
        >
          <i class="fi fi-sr-check-circle text-base leading-none text-primary" />
          Добавлено · {{ basketTotal.toLocaleString("ru-RU") }} ₽
        </button>
        <button
          v-else-if="defaultPrice"
          type="button"
          class="h-[50px] flex-1 rounded-2xl bg-primary text-sm text-white transition-opacity hover:opacity-80"
          @click="addToBasket(defaultPrice)"
        >
          В корзину · {{ getFormat(defaultPrice).label }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { ApiKey } from "~/common/API/baseApi";
  import type { FullPlace, Place, PlacePrice, ReducedPlace } from "~/interfaces/publisher";
  import IPlus from "~/components/UINew/icons/IPlus.vue";
  import IMinus from "~/components/UINew/icons/IMinus.vue";
  import PlaceService from "~/services/PlaceService";
  import BasketService from "~/services/BasketService";
  import { useBasketStore } from "~/store/BasketModule";
  import { useUserStore } from "~/store/UserModule";

  const emit = defineEmits<{ (e: "close"): void; (e: "message", place: any): void }>();

  const route = useRoute();
  const userModule = useUserStore();
  const basketStore = useBasketStore();
  const { basket } = storeToRefs(basketStore);

  const place = ref<FullPlace | null>(null);
  const isLoading = ref(true);
  const activeTab = ref(0);
  const tabs = ["Купить рекламу", "Расширенная статистика", "Другие каналы владельца"];

  const isAuth = () => !!ApiKey.get();

  const load = async () => {
    const slug = (route.query.id as string) || "";
    if (!slug) return;
    isLoading.value = true;
    activeTab.value = 0;
    try {
      place.value = (await PlaceService.getPlace(undefined, slug)) as FullPlace;
    } finally {
      isLoading.value = false;
    }
  };
  watch(() => route.query.id, load, { immediate: true });

  // Описание сворачиваем, как на странице канала
  const DESCRIPTION_MAX_LENGTH = 200;
  const isDescriptionExpanded = ref(false);
  const isDescriptionTruncatable = computed(
    () => (place.value?.description?.length || 0) > DESCRIPTION_MAX_LENGTH
  );
  const displayedDescription = computed(() => {
    const description = place.value?.description || "";
    if (!isDescriptionTruncatable.value || isDescriptionExpanded.value) return description;
    return description.slice(0, DESCRIPTION_MAX_LENGTH).trimEnd() + "...";
  });

  const FORMATS: Record<string, { label: string; hint: string }> = {
    post24h: { label: "Пост 24ч", hint: "24 часа в ленте" },
    post48h: { label: "Пост 48ч", hint: "48 часов в ленте" },
    post72h: { label: "Пост 72ч", hint: "72 часа в ленте" },
    post30d: { label: "Пост 30 дней", hint: "30 дней в ленте" },
    fixedInTop: { label: "Пост 30 дней", hint: "30 дней в ленте" },
    repost: { label: "Репост", hint: "Репост записи, 24 часа в ленте" },
    story: { label: "Сторис", hint: "Публикация в сторис канала на 24 часа" },
    native: { label: "Натив", hint: "Нативное размещение в контенте канала" },
  };
  const FORMAT_ORDER = Object.keys(FORMATS);
  const getFormat = (price: PlacePrice) =>
    FORMATS[price.priceType] || { label: price.name || price.priceType, hint: "" };

  const sortedPrices = computed(
    () =>
      place.value?.prices
        ?.slice()
        .sort((a, b) => FORMAT_ORDER.indexOf(a.priceType) - FORMAT_ORDER.indexOf(b.priceType)) || []
  );
  const defaultPrice = computed(() => sortedPrices.value[0] ?? null);

  // Сумма по этому каналу в корзине (все форматы × количество)
  const basketTotal = computed(() =>
    basket.value
      .filter((item) => item.id === place.value?.id)
      .reduce((sum, item) => sum + (item.format?.price || 0) * (item.quantity || 1), 0)
  );

  const slides = [
    {
      icon: "fi-sr-comment",
      title: "Вопрос до заказа",
      text: "Уточните формат, сроки или креатив — переписка попадёт в «Диалоги» как запрос, без оформления сделки.",
    },
    {
      icon: "fi-sr-shield-check",
      title: "Безопасная сделка",
      text: "После оплаты деньги резервируются и переводятся каналу только после вашего подтверждения публикации.",
    },
    {
      icon: "fi-sr-rocket-lunch",
      title: "Как проходит заказ",
      text: "Создайте пост и укажите дату — владелец опубликует его и пришлёт ссылку, вам останется подтвердить.",
    },
    {
      icon: "fi-sr-file-signature",
      title: "Маркировка постов",
      text: "Укажите реквизиты при создании поста — Телепост зарегистрирует его в ОРД и получит erid автоматически.",
    },
  ];
  const activeSlide = ref(0);
  const slide = computed(() => slides[activeSlide.value]);
  let slideTimer: ReturnType<typeof setInterval> | null = null;
  const startSlides = () => {
    stopSlides();
    slideTimer = setInterval(() => {
      activeSlide.value = (activeSlide.value + 1) % slides.length;
    }, 5000);
  };
  const stopSlides = () => {
    if (slideTimer) clearInterval(slideTimer);
    slideTimer = null;
  };
  const goToSlide = (index: number) => {
    activeSlide.value = index;
    startSlides();
  };
  onMounted(startSlides);
  onUnmounted(stopSlides);

  // Корзина — та же логика, что у форматов на странице канала
  const findBasketItem = (price: PlacePrice) =>
    basket.value.find(
      (item) => item.id === place.value?.id && item.format?.priceType === price.priceType
    );

  const addToBasket = (price: PlacePrice) => {
    const current = place.value;
    if (!current) return;
    const existing = findBasketItem(price);
    if (existing) {
      existing.quantity = Math.min(10, (existing.quantity || 1) + 1);
      return;
    }
    // Копия без ссылок на объект канала: в корзине он живёт своей жизнью
    const basketItem = JSON.parse(
      JSON.stringify({
        id: current.id,
        name: current.name,
        url: current.url,
        avatar: current.avatar,
        format: price,
        quantity: 1,
        categories: current.categories,
        subscribersStatistic: current.subscribersStatistic,
        prices: current.prices,
        viewsStatistic: current.viewsStatistic,
        er: current.er,
      })
    );
    BasketService.addPlace(
      basketItem as Place & { format: PlacePrice | null; subscribersStatistic: { count: number } }
    );
    basket.value.push(basketItem as ReducedPlace);
  };

  const increaseQuantity = (price: PlacePrice) => {
    const item = findBasketItem(price);
    if (item && (item.quantity || 1) < 10) item.quantity = (item.quantity || 1) + 1;
  };

  const decreaseQuantity = (price: PlacePrice) => {
    const item = findBasketItem(price);
    if (!item || !place.value) return;
    if ((item.quantity || 1) > 1) {
      item.quantity = (item.quantity || 1) - 1;
      return;
    }
    basket.value = basket.value.filter(
      (p: ReducedPlace) => !(p.id === place.value?.id && p.format?.priceType === price.priceType)
    );
    BasketService.removePlace(place.value.id, price);
  };

  const toggleFavorite = async () => {
    const current = place.value;
    if (!current) return;
    if (!isAuth()) {
      userModule.loginDialogVisible = true;
      return;
    }
    const social = current.social || "telegram";
    try {
      if (current.isFavorite) {
        await PlaceService.removePlaceFromFavorite({ id: current.id, social });
        current.isFavorite = false;
      } else {
        await PlaceService.addPlaceToFavorite({ placeId: current.id, social });
        current.isFavorite = true;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const shareChannel = () => {
    navigator.clipboard.writeText(`${window.location.origin}/ch/${place.value?.slug}`);
    ElNotification.success("Ссылка на канал скопирована в буфер обмена");
  };

  const askQuestion = (target: any = place.value) => {
    if (!isAuth()) {
      userModule.loginDialogVisible = true;
      return;
    }
    emit("message", { ...target, format: target?.format ?? defaultPrice.value });
  };
</script>

<style lang="scss" scoped>
  .channel-sidebar-slide-enter-active,
  .channel-sidebar-slide-leave-active {
    transition: opacity 0.25s ease;
  }
  .channel-sidebar-slide-enter-from,
  .channel-sidebar-slide-leave-to {
    opacity: 0;
  }

  .channel-sidebar__round {
    /* Кнопки лежат на размытой обложке: полупрозрачный ховер «пропадал»,
       поэтому затемняем сам белый фон */
    @apply flex size-10 items-center justify-center rounded-full bg-white text-base leading-none transition-colors hover:bg-[#E8EFFF];

    /* У глифов иконочного шрифта рисунок смещён вверх относительно строки */
    i {
      @apply translate-y-px;
    }
  }
</style>
